import { useCallback, useEffect, useState } from 'react';
import { request } from '../http.ts';

/** Every path here hangs off the knowledge router, so the prefix is written once. */
const call = <T,>(path: string, init?: RequestInit) => request<T>(`/knowledge${path}`, init);

/**
 * Things runs believe, waiting for somebody to agree.
 *
 * Nothing on this screen has affected a single run. Each card is a sentence a
 * step came away from its work convinced of, and accepting one commits it into
 * a skill file — which is why the claim itself is the whole first line and the
 * evidence is behind a disclosure. A reviewer who accepts from a wall of
 * transcript is a reviewer teaching every future run whatever the transcript
 * happened to say.
 */
const STATES = ['proposed', 'accepted', 'rejected'] as const;

type State = (typeof STATES)[number];

// Local rather than in `api.ts` because this screen turns on the two answers
// the shared client flattens into a message: no GitHub App, and somebody else
// decided first.
interface KnowledgeProposalSummary {
  id: string;
  skillName: string;
  claim: string;
  runId: string | null;
  stepId: string | null;
  evidence: string | null;
  state: string;
  proposedBy: string | null;
  proposedByEmail: string | null;
  createdAt: string;
  decidedBy: string | null;
  decidedAt: string | null;
  commitSha: string | null;
}

export function KnowledgeView({ onChanged }: { onChanged?: () => void } = {}) {
  const [state, setState] = useState<State>('proposed');
  const [proposals, setProposals] = useState<KnowledgeProposalSummary[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const refresh = useCallback(() => {
    call<{ proposals: KnowledgeProposalSummary[] }>(`?state=${state}`)
      .then((result) => setProposals(result.proposals))
      .catch((error) => setNote((error as Error).message));
  }, [state]);

  useEffect(refresh, [refresh]);

  const act = async (id: string, label: string, work: () => Promise<string>) => {
    setBusy(`${id}:${label}`);
    setNote(null);
    try {
      setNote(await work());
      refresh();
      // The tab carries the waiting count, and a proposal decided here has just
      // changed it. Without this the badge keeps offering work that is done.
      onChanged?.();
    } catch (error) {
      setNote((error as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="body">
      <aside className="sidebar">
        <p className="section-title">Knowledge</p>
        {STATES.map((entry) => (
          <button
            key={entry}
            className="list-item"
            aria-current={entry === state}
            onClick={() => setState(entry)}
          >
            {LABELS[entry]}
          </button>
        ))}
      </aside>

      <main className="main">
        <h1 style={{ margin: '0 0 0.25rem', fontSize: '1.1rem' }}>{LABELS[state]}</h1>
        <p className="hint" style={{ marginTop: 0 }}>
          {DESCRIPTIONS[state]}
        </p>

        {note && (
          <div className="banner">
            <p style={{ margin: 0 }}>{note}</p>
          </div>
        )}

        {proposals.length === 0 ? (
          <div className="empty">
            {state === 'proposed' ? 'Nothing waiting.' : `Nothing ${state} yet.`}
          </div>
        ) : (
          <div className="stack">
            {proposals.map((proposal) => (
              <div className="card" key={proposal.id}>
                <p style={{ margin: '0 0 0.4rem' }}>{proposal.claim}</p>

                <p className="hint" style={{ margin: '0 0 0.6rem' }}>
                  <span className="pill">{proposal.skillName}</span>
                  {proposal.runId ? ` · learned in run ${proposal.runId}` : ' · proposed by hand'}
                  {proposal.stepId && ` · step ${proposal.stepId}`}
                  {/* The address when reef knows one; the id only when it does not. */}
                  {proposal.proposedBy &&
                    ` · from ${proposal.proposedByEmail ?? proposal.proposedBy}`} ·{' '}
                  {new Date(proposal.createdAt).toLocaleString()}
                  {proposal.decidedBy && ` · decided by ${proposal.decidedBy}`}
                </p>

                {/*
                 * The commit is shown rather than the fact of acceptance,
                 * because the commit is the thing that changed the agent and
                 * the thing somebody would revert.
                 */}
                {proposal.commitSha && (
                  <p className="hint" style={{ margin: '0 0 0.6rem' }}>
                    Committed to <code>skills/{proposal.skillName}/SKILL.md</code> as{' '}
                    <code>{proposal.commitSha.slice(0, 12)}</code> — <code>git revert</code> undoes
                    it.
                  </p>
                )}

                {proposal.evidence && (
                  <details
                    open={expanded === proposal.id}
                    onToggle={(event) =>
                      setExpanded(
                        (event.currentTarget as HTMLDetailsElement).open ? proposal.id : null,
                      )
                    }
                  >
                    <summary className="hint">What the run saw</summary>
                    <pre style={{ overflowX: 'auto', whiteSpace: 'pre-wrap' }}>
                      {proposal.evidence}
                    </pre>
                  </details>
                )}

                {proposal.state === 'proposed' && (
                  <div className="row wrap">
                    <button
                      className="primary"
                      disabled={busy !== null}
                      onClick={() =>
                        act(proposal.id, 'accept', async () => {
                          const result = await call<{ commit: string; path: string }>(
                            `/${encodeURIComponent(proposal.id)}/accept`,
                            { method: 'POST' },
                          );
                          return `Committed to ${result.path} as ${result.commit.slice(0, 12)}. Every run of every workflow using that skill is told this from now on.`;
                        })
                      }
                    >
                      Accept
                    </button>
                    <button
                      disabled={busy !== null}
                      onClick={() =>
                        act(proposal.id, 'reject', async () => {
                          await call(`/${encodeURIComponent(proposal.id)}/reject`, {
                            method: 'POST',
                            body: JSON.stringify({}),
                          });
                          return 'Rejected. Nothing was written, and no run behaves differently.';
                        })
                      }
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="row">
          <button onClick={refresh}>Refresh</button>
        </div>
      </main>
    </div>
  );
}

const LABELS: Record<State, string> = {
  proposed: 'Waiting for you',
  accepted: 'Accepted',
  rejected: 'Rejected',
};

const DESCRIPTIONS: Record<State, string> = {
  proposed:
    'A run came away from its work believing each of these. None of them has changed a single run — accepting one commits it into the skill, where every future run reads it.',
  accepted:
    'These are in the skill files now, one commit each, attributed to whoever accepted them. Reverting one is a revert of that commit.',
  rejected: 'These were refused. Nothing was written, and the runs that proposed them are unaffected.',
};
